/**
 * Quote filtering service - pure functions for filtering quotes
 */

/**
 * Get quotes that are active (quoted or ready)
 * @param {Array} quotes - Array of quote objects
 * @returns {Array} - Filtered quotes
 */
export function getActiveQuotes(quotes) {
  if (!Array.isArray(quotes)) return [];

  return quotes.filter((quote) => {
    const carrierStatus = quote.payToCarrierQuoteStatus?.toLowerCase();
    const status = quote.status?.toLowerCase();
    return carrierStatus === "quoted" || status === "quote_ready";
  });
}

/**
 * Get quotes that are inactive (not quoted/ready)
 * @param {Array} quotes - Array of quote objects
 * @returns {Array} - Filtered quotes
 */
export function getInactiveQuotes(quotes) {
  if (!Array.isArray(quotes)) return [];

  return quotes.filter((quote) => {
    const carrierStatus = quote.payToCarrierQuoteStatus?.toLowerCase();
    const status = quote.status?.toLowerCase();
    const isActive = carrierStatus === "quoted" || status === "quote_ready";
    return !isActive;
  });
}

/**
 * Get quotes filtered by carrier selection
 * @param {Array} quotes - Array of quote objects
 * @param {Array} carrierList - Array of { name, selected } carrier objects
 * @returns {Array} - Filtered quotes
 */
export function getFilteredByCarrier(quotes, carrierList) {
  if (!Array.isArray(quotes) || !Array.isArray(carrierList)) return [];

  return quotes.filter((quote) => {
    const admittedStatus = quote.admittedInd ? "Admitted" : "Non-Admitted";
    const carrierName = quote.payToCarrierName || "";
    const fullName = `${carrierName} ${admittedStatus} ${quote.amBestRating || ""}`.trim();

    return carrierList.some((carrier) => carrier.name?.toLowerCase() === fullName?.toLowerCase() && carrier.selected);
  });
}

/**
 * Get bound policies from both quick and full quotes
 * @param {Object} quickQuotes - Quick quotes object with quotes array
 * @param {Object} fullQuotes - Full quotes object with quotes array
 * @returns {Array} - Array of bound policies with quote_type property set
 */
export function getActiveBoundPolicies(quickQuotes, fullQuotes) {
  const quickQuotesList = quickQuotes?.quotes || [];
  const fullQuotesList = fullQuotes?.quotes || [];

  const boundQuick = quickQuotesList
    .filter((quote) => quote.payToCarrierQuoteStatus?.toLowerCase() === "bound" || quote.bind_status === "completed")
    .map((quote) => ({
      ...quote,
      quote_type: "quick_quotes",
    }));

  const boundFull = fullQuotesList
    .filter((quote) => quote.payToCarrierQuoteStatus?.toLowerCase() === "bound" || quote.bind_status === "completed")
    .map((quote) => ({
      ...quote,
      quote_type: "full_quotes",
    }));

  return [...boundQuick, ...boundFull];
}

export default {
  getActiveQuotes,
  getInactiveQuotes,
  getFilteredByCarrier,
  getActiveBoundPolicies,
};
